import React, { useState } from "react";
import { api } from "@/lib/api";
import { Download } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";

const REPORTS = [
  { id: "current-stock", label: "Current Stock", params: [] },
  { id: "ledger", label: "Stock Ledger", params: ["item_id"] },
  { id: "supplier-consumption", label: "Consumption by Code", params: ["item_id", "start", "end"] },
  { id: "aging", label: "Batch & Container Aging", params: [] },
  { id: "low-stock", label: "Low Stock", params: [] },
  { id: "packaging-usage", label: "Packaging Usage", params: ["start", "end"] },
  { id: "user-activity", label: "User Activity", params: ["start", "end"] },
  { id: "production-cost", label: "Production Batch Cost", params: ["start", "end"] },
];

export default function Reports() {
  const [active, setActive] = useState(REPORTS[0]);
  const [params, setParams] = useState({});
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const { data: items = [] } = useQuery({
    queryKey: ["items-list-report"],
    queryFn: async () => (await api.get("/items")).data,
  });

  const run = async () => {
    setLoading(true); setRows(null);
    try {
      const r = await api.get(`/reports/${active.id}`, { params });
      setRows(r.data);
    } catch (e) { toast.error("Failed to run report"); }
    finally { setLoading(false); }
  };

  const download = () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/reports/${active.id}?${new URLSearchParams({ ...params, format: "csv" }).toString()}`;
    // include cookie via credentials-enabled anchor download workaround: fetch then blob
    fetch(url, { credentials: "include", headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` } })
      .then(r => r.blob())
      .then(b => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(b);
        a.download = `${active.id}.csv`;
        a.click();
      })
      .catch(() => toast.error("Download failed"));
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="reports-page">
      <div>
        <div className="data-label">Analytics</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold">Reports</h1>
      </div>
      <div className="flex flex-wrap gap-2">
        {REPORTS.map(r => (
          <button
            key={r.id}
            onClick={() => { setActive(r); setRows(null); setParams({}); }}
            data-testid={`report-tab-${r.id}`}
            className={`text-xs uppercase tracking-widest font-semibold px-3 py-2 rounded-sm border ${
              active.id === r.id
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-white border-border text-foreground"
            }`}
          >{r.label}</button>
        ))}
      </div>
      <div className="bg-white border border-border rounded-sm p-4 space-y-3">
        <div className="grid md:grid-cols-3 gap-3">
          {active.params.includes("item_id") && (
            <label>
              <span className="data-label">Item</span>
              <select className="field-sm mt-1" value={params.item_id || ""}
                      onChange={(e) => setParams(p => ({ ...p, item_id: e.target.value }))}
                      data-testid="report-item">
                <option value="">Select…</option>
                {items.map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
              </select>
            </label>
          )}
          {active.params.includes("start") && (
            <label>
              <span className="data-label">Start</span>
              <input type="date" className="field-sm mt-1" value={params.start || ""}
                     onChange={(e) => setParams(p => ({ ...p, start: e.target.value + "T00:00:00" }))}
                     data-testid="report-start" />
            </label>
          )}
          {active.params.includes("end") && (
            <label>
              <span className="data-label">End</span>
              <input type="date" className="field-sm mt-1" value={params.end?.slice(0,10) || ""}
                     onChange={(e) => setParams(p => ({ ...p, end: e.target.value + "T23:59:59" }))}
                     data-testid="report-end" />
            </label>
          )}
        </div>
        <div className="flex gap-2">
          <button onClick={run} className="btn-primary" disabled={loading} data-testid="report-run">
            {loading ? "Running…" : "Run"}
          </button>
          {rows && rows.length > 0 && (
            <button onClick={download} className="btn-ghost" data-testid="report-download">
              <Download className="w-4 h-4 mr-1" /> CSV
            </button>
          )}
        </div>
      </div>
      {rows && (
        <div className="bg-white border border-border rounded-sm overflow-auto" data-testid="report-results">
          {rows.length === 0 ? (
            <div className="p-6 text-sm text-muted-foreground">No rows.</div>
          ) : (
            (() => {
              const keys = Object.keys(rows[0]).filter(k => !Array.isArray(rows[0][k]));
              return (
                <table className="w-full text-sm">
                  <thead className="bg-muted">
                    <tr>{keys.map(k => (
                      <th key={k} className="text-left px-3 py-2 data-label">{k}</th>
                    ))}</tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={i} className="border-t border-border row-hover">
                        {keys.map(k => (
                          <td key={k} className="px-3 py-2 font-mono text-xs whitespace-nowrap">
                            {r[k] === null ? <span className="text-muted-foreground italic">—</span>
                              : typeof r[k] === "boolean" ? String(r[k])
                              : typeof r[k] === "number" ? r[k].toLocaleString(undefined, { maximumFractionDigits: 4 })
                              : String(r[k] ?? "")}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              );
            })()
          )}
        </div>
      )}
    </div>
  );
}
